import { useState } from 'react'
import { toast } from 'sonner'
import { Copy, Link2 } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { pjAgradaService } from '@/services/pjAgradaService'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { formatSecurityDate } from '@/components/Configuracao/behavioralSecurityHelpers'

type Props = {
  open: boolean
  onClose: () => void
  onGenerated?: () => void
}

export function PjAgradaInviteModal({ open, onClose, onGenerated }: Props) {
  const [nomeCompleto, setNomeCompleto] = useState('')
  const [email, setEmail] = useState('')
  const [areaAtuacao, setAreaAtuacao] = useState('')
  const [expiresInHours, setExpiresInHours] = useState('72')
  const [generating, setGenerating] = useState(false)
  const [inviteUrl, setInviteUrl] = useState('')
  const [expiresAt, setExpiresAt] = useState('')

  const reset = () => {
    setNomeCompleto('')
    setEmail('')
    setAreaAtuacao('')
    setExpiresInHours('72')
    setInviteUrl('')
    setExpiresAt('')
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault()
    setGenerating(true)
    try {
      const result = await pjAgradaService.createInvite({
        nomeCompleto: nomeCompleto.trim() || undefined,
        email: email.trim() || undefined,
        areaAtuacao: areaAtuacao.trim() || undefined,
        expiresInHours: Number(expiresInHours) || 72,
      })
      setInviteUrl(result.url)
      setExpiresAt(result.expiresAt)
      onGenerated?.()
      toast.success('Link de cadastro gerado')
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao gerar link'))
    } finally {
      setGenerating(false)
    }
  }

  const copyLink = async () => {
    if (!inviteUrl) return
    try {
      await navigator.clipboard.writeText(inviteUrl)
      toast.success('Link copiado')
    } catch {
      toast.error('Não foi possível copiar o link')
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title='Link de auto-cadastro'
      description='Envie ao colaborador PJ para ele preencher os próprios dados. O link é único e expira automaticamente.'
      size='lg'
      footer={
        inviteUrl ? (
          <>
            <Button type='button' variant='secondary' onClick={handleClose}>
              Fechar
            </Button>
            <Button type='button' variant='primary' icon={<Copy size={14} />} onClick={() => void copyLink()}>
              Copiar link
            </Button>
          </>
        ) : (
          <>
            <Button type='button' variant='secondary' onClick={handleClose}>
              Cancelar
            </Button>
            <Button type='submit' form='pj-invite-form' disabled={generating}>
              {generating ? 'Gerando…' : 'Gerar link'}
            </Button>
          </>
        )
      }
    >
      {inviteUrl ? (
        <div className='space-y-4'>
          <div className='rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] p-4'>
            <p className='text-xs uppercase tracking-wide text-[var(--font-color-label)] mb-2'>
              Link para o colaborador
            </p>
            <p className='break-all font-mono text-sm text-[var(--font-color-body)]'>{inviteUrl}</p>
            <p className='mt-2 text-xs text-[var(--font-color-muted)]'>
              Expira em {formatSecurityDate(expiresAt)} · uso único
            </p>
          </div>
          <p className='text-sm text-[var(--font-color-muted)]'>
            O colaborador poderá informar documentos, endereço e dados bancários. O financeiro revisa depois na lista.
          </p>
        </div>
      ) : (
        <form id='pj-invite-form' onSubmit={(e) => void handleGenerate(e)} className='space-y-4'>
          <div className='flex items-center gap-2 rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 py-2 text-sm text-[var(--font-color-muted)]'>
            <Link2 size={16} className='shrink-0 text-[var(--brand-primary)]' />
            Campos opcionais abaixo pré-preenchem o formulário do colaborador.
          </div>
          <Input
            label='Nome (opcional)'
            value={nomeCompleto}
            onChange={(e) => setNomeCompleto(e.target.value)}
            placeholder='Se já souber o nome'
          />
          <Input
            label='E-mail (recomendado)'
            type='email'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            hint='Se informado, o colaborador só consegue concluir o cadastro com este e-mail.'
          />
          <Input
            label='Cargo / setor (opcional)'
            value={areaAtuacao}
            onChange={(e) => setAreaAtuacao(e.target.value)}
            placeholder='Ex: Designer, Closer'
          />
          <div>
            <label className='block text-[var(--font-color-label)] mb-1.5' style={{ fontSize: 'var(--font-size-label)' }}>
              Validade do link (horas)
            </label>
            <select
              value={expiresInHours}
              onChange={(e) => setExpiresInHours(e.target.value)}
              className='w-full rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 py-2 text-sm'
            >
              <option value='24'>24 horas</option>
              <option value='48'>48 horas</option>
              <option value='72'>72 horas (padrão)</option>
              <option value='168'>7 dias</option>
            </select>
          </div>
        </form>
      )}
    </Modal>
  )
}
